import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button } from 'stories/components'
import { confirm, InputConvert } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import {
  asPrepaymentPenaltyList,
  getBasedOnOptions,
  getBasicInputs,
  getFlatPenaltyInputs,
  getInterestBasedInputs,
  getPenaltyTypeOptions,
  getStepdownEntryInputs,
  prepaymentPenaltiesWithIds,
  PrepaymentPenalty,
  StepdownEntry,
} from './constant'

export const PrepaymentPenaltyConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [penalties, setPenalties] = useState<PrepaymentPenalty[]>([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingPenalty, setEditingPenalty] = useState<PrepaymentPenalty | null>(null)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [inputs, setInputs] = useState<Record<string, any>>(getBasicInputs())
  const [selectedType, setSelectedType] = useState<string>('flat')

  // For Stepdown/Other type
  const [stepdownEntries, setStepdownEntries] = useState<StepdownEntry[]>([
    { percentage: '', startTerm: 1, endTerm: 12 },
  ])

  useEffect(() => {
    fetchPenalties()
  }, [])

  const fetchPenalties = async () => {
    setLoading(true)
    try {
      const data = await getAdminConfig('prepaymentPenalties')
      setPenalties(asPrepaymentPenaltyList(data))
    } catch (error) {
      console.error('Error fetching prepayment penalties:', error)
      setPenalties([])
    } finally {
      setLoading(false)
    }
  }

  const savePenalties = async (newPenalties: PrepaymentPenalty[]) => {
    const toSave = prepaymentPenaltiesWithIds(newPenalties)
    setLoading(true)
    try {
      await setAdminConfig('prepaymentPenalties', toSave)
      setPenalties(toSave)
    } catch (error) {
      console.error('Error saving prepayment penalties:', error)
    } finally {
      setLoading(false)
    }
  }

  const validateStepdownEntries = (): boolean => {
    for (const entry of stepdownEntries) {
      if (!entry.percentage || entry.percentage.trim() === '') {
        toast('Please enter percentage for all stepdown entries', { type: 'error' })
        return false
      }
      if (entry.startTerm <= 0 || entry.endTerm <= 0 || entry.startTerm > entry.endTerm) {
        toast('Please enter valid term ranges for all stepdown entries', { type: 'error' })
        return false
      }
    }
    return true
  }

  const handleAddPenalty = () => {
    if (!inputs.label.value || inputs.label.value.trim() === '') {
      toast('Please enter a label', { type: 'error' })
      return
    }

    const type = inputs.type.value || 'flat'
    let penaltyFee: string | StepdownEntry[]

    if (type === 'flat') {
      const flatPercentage = inputs.flatPercentage?.value || ''
      if (!flatPercentage || flatPercentage.trim() === '') {
        toast('Please enter penalty fee percentage', { type: 'error' })
        return
      }
      penaltyFee = flatPercentage
    } else if (type === 'stepdown') {
      if (!validateStepdownEntries()) return
      penaltyFee = cloneDeep(stepdownEntries)
    } else {
      // Interest Based
      const interestMonths = inputs.interestMonths?.value || ''
      if (!interestMonths || interestMonths.trim() === '') {
        toast('Please enter months of interest', { type: 'error' })
        return
      }
      penaltyFee = interestMonths
    }

    const penaltyData: PrepaymentPenalty = {
      id: crypto.randomUUID(),
      label: inputs.label.value,
      type: type as PrepaymentPenalty['type'],
      penaltyFee,
      basedOn: inputs.basedOn.value || 'cupb',
      maxPrepaymentPercentage: Number(inputs.maxPrepaymentPercentage.value) || 0,
    }

    const newPenalties = [...penalties, penaltyData]
    savePenalties(newPenalties)
    setShowAddModal(false)
    resetForm()
  }

  const handleUpdatePenalty = () => {
    if (editingIndex === null) return

    if (!inputs.label.value || inputs.label.value.trim() === '') {
      toast('Please enter a label', { type: 'error' })
      return
    }

    const type = inputs.type.value || 'flat'
    let penaltyFee: string | StepdownEntry[]

    if (type === 'flat') {
      const flatPercentage = inputs.flatPercentage?.value || ''
      if (!flatPercentage || flatPercentage.trim() === '') {
        toast('Please enter penalty fee percentage', { type: 'error' })
        return
      }
      penaltyFee = flatPercentage
    } else if (type === 'stepdown') {
      if (!validateStepdownEntries()) return
      penaltyFee = cloneDeep(stepdownEntries)
    } else {
      // Interest Based
      const interestMonths = inputs.interestMonths?.value || ''
      if (!interestMonths || interestMonths.trim() === '') {
        toast('Please enter months of interest', { type: 'error' })
        return
      }
      penaltyFee = interestMonths
    }

    const penaltyData: PrepaymentPenalty = {
      id: editingPenalty?.id ?? crypto.randomUUID(),
      label: inputs.label.value,
      type: type as PrepaymentPenalty['type'],
      penaltyFee,
      basedOn: inputs.basedOn.value || 'cupb',
      maxPrepaymentPercentage: Number(inputs.maxPrepaymentPercentage.value) || 0,
    }

    const newPenalties = [...penalties]
    newPenalties[editingIndex] = penaltyData
    savePenalties(newPenalties)
    setEditingPenalty(null)
    setEditingIndex(null)
    setShowAddModal(false)
    resetForm()
  }

  const resetForm = () => {
    const newInputs = cloneDeep(getBasicInputs())
    newInputs.label.value = ''
    newInputs.type.value = 'flat'
    newInputs.basedOn.value = 'cupb'
    newInputs.maxPrepaymentPercentage.value = ''
    // Always include fee field for default type; useEffect does not run when selectedType stays 'flat'
    newInputs.flatPercentage = getFlatPenaltyInputs().flatPercentage
    setInputs(newInputs)
    setSelectedType('flat')
    setStepdownEntries([{ percentage: '', startTerm: 1, endTerm: 12 }])
  }

  const onChange = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)

    // Track type changes
    if (key === 'type') {
      setSelectedType(value)
    }
  }

  const movePenalty = (index: number, direction: 'up' | 'down') => {
    const nextIndex = direction === 'up' ? index - 1 : index + 1
    if (nextIndex < 0 || nextIndex >= penalties.length) return
    const next = [...penalties]
    ;[next[index], next[nextIndex]] = [next[nextIndex], next[index]]
    savePenalties(next)
  }

  const handleDeletePenalty = async (index: number) => {
    const penalty = penalties[index]
    const content = `Are you sure you want to delete "${penalty.label}"?`
    const isContinue = await confirm(content)
    if (!isContinue) return
    const newPenalties = penalties.filter((_, i) => i !== index)
    savePenalties(newPenalties)
  }

  const openAddModal = () => {
    setEditingPenalty(null)
    setEditingIndex(null)
    resetForm()
    setShowAddModal(true)
  }

  const openEditModal = (penalty: PrepaymentPenalty, index: number) => {
    setEditingPenalty(penalty)
    setEditingIndex(index)
    const newInputs = cloneDeep(getBasicInputs())
    newInputs.label.value = penalty.label
    newInputs.type.value = penalty.type
    newInputs.basedOn.value = penalty.basedOn
    newInputs.maxPrepaymentPercentage.value = penalty.maxPrepaymentPercentage

    setSelectedType(penalty.type)

    if (penalty.type === 'flat') {
      newInputs.flatPercentage = getFlatPenaltyInputs().flatPercentage
      newInputs.flatPercentage.value = penalty.penaltyFee as string
    } else if (penalty.type === 'stepdown') {
      setStepdownEntries(cloneDeep(penalty.penaltyFee as StepdownEntry[]))
    } else {
      // Interest Based
      newInputs.interestMonths = getInterestBasedInputs().interestMonths
      newInputs.interestMonths.value = penalty.penaltyFee as string
    }

    setInputs(newInputs)
    setShowAddModal(true)
  }

  const addStepdownEntry = () => {
    const lastEntry = stepdownEntries[stepdownEntries.length - 1]
    const newStart = lastEntry ? lastEntry.endTerm + 1 : 1
    const newEnd = newStart + 11
    setStepdownEntries([...stepdownEntries, { percentage: '', startTerm: newStart, endTerm: newEnd }])
  }

  const removeStepdownEntry = (index: number) => {
    if (stepdownEntries.length === 1) {
      toast('At least one stepdown entry is required', { type: 'error' })
      return
    }
    const newEntries = stepdownEntries.filter((_, i) => i !== index)
    setStepdownEntries(newEntries)
  }

  const updateStepdownEntry = (index: number, field: keyof StepdownEntry, value: string | number) => {
    const newEntries: any = cloneDeep(stepdownEntries)
    newEntries[index][field] = value as any
    setStepdownEntries(newEntries)
  }

  const renderPenaltyFee = (penalty: PrepaymentPenalty) => {
    if (penalty.type === 'flat') {
      return `${penalty.penaltyFee}%`
    } else if (penalty.type === 'stepdown') {
      const entries = penalty.penaltyFee as StepdownEntry[]
      return (
        <div className="text-xs">
          {entries.map((entry, i) => (
            <div key={i}>
              {entry.percentage}% (Term {entry.startTerm}-{entry.endTerm})
            </div>
          ))}
        </div>
      )
    } else {
      // Interest Based
      return `${penalty.penaltyFee} months of interest`
    }
  }

  // Keep only the penalty-fee inputs that match the selected type (avoid stale fields when switching)
  useEffect(() => {
    setInputs((prev) => {
      const next = { ...prev }
      if (selectedType !== 'flat') {
        delete next.flatPercentage
      }
      if (selectedType !== 'interestBased') {
        delete next.interestMonths
      }
      if (selectedType === 'flat' && !next.flatPercentage) {
        next.flatPercentage = getFlatPenaltyInputs().flatPercentage
      } else if (selectedType === 'interestBased' && !next.interestMonths) {
        next.interestMonths = getInterestBasedInputs().interestMonths
      }
      return next
    })
  }, [selectedType])

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />

      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-semibold">Prepayment Penalty Configuration</h3>
        <Button onClick={openAddModal} className="flex items-center gap-2">
          <span className="flex items-center gap-2">
            <PlusIcon className="w-5 h-5" />
            Add Penalty
          </span>
        </Button>
      </div>

      {/* Penalties List */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="space-y-3">
          {penalties.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No prepayment penalties configured</p>
          ) : (
            penalties.map((penalty, index) => (
              <div
                key={penalty.id ?? `${index}-${penalty.label}`}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex flex-col gap-1 shrink-0 pt-1">
                    <button
                      type="button"
                      title="Move up"
                      disabled={index === 0}
                      onClick={() => movePenalty(index, 'up')}
                      className="text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:pointer-events-none p-0.5"
                    >
                      <ArrowUpIcon className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      title="Move down"
                      disabled={index === penalties.length - 1}
                      onClick={() => movePenalty(index, 'down')}
                      className="text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:pointer-events-none p-0.5"
                    >
                      <ArrowDownIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="font-semibold text-base mb-2">{penalty.label}</h5>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Type:</span>{' '}
                        <span className="font-medium">{getPenaltyTypeOptions()[penalty.type]}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-600">Penalty Fee:</span>{' '}
                        <div className="font-medium inline-block">{renderPenaltyFee(penalty)}</div>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-600">Based On:</span>{' '}
                        <span className="font-medium capitalize">{getBasedOnOptions()[penalty.basedOn]}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-600">Max Prepayment Without Penalty:</span>{' '}
                        <span className="font-medium">{penalty.maxPrepaymentPercentage}% per year</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => openEditModal(penalty, index)}
                      className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-600 rounded hover:bg-blue-50"
                    >
                      Edit
                    </button>
                    <button onClick={() => handleDeletePenalty(index)} className="text-red-600 hover:text-red-800 p-1">
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">
              {editingPenalty ? 'Edit Prepayment Penalty' : 'Add Prepayment Penalty'}
            </h3>

            <div className="space-y-4">
              {/* Render inputs dynamically using Object.keys */}
              {Object.keys(inputs).map((key) => {
                const input = inputs[key]

                return (
                  <div key={key}>
                    {/* Render regular input field */}
                    <RenderInput input={input} Key={key} onChange={onChange} />

                    {/* Insert stepdown schedule after 'type' field if stepdown is selected */}
                    {key === 'type' && selectedType === 'stepdown' && (
                      <div className="border border-gray-300 rounded-lg p-4 mt-4">
                        <div className="flex justify-between items-center mb-3">
                          <label className="block text-sm font-medium text-gray-700">Stepdown Schedule</label>
                          <Button onClick={addStepdownEntry} className="text-sm py-1 px-3">
                            Add Step
                          </Button>
                        </div>
                        <div className="space-y-3">
                          {stepdownEntries.map((entry, index) => {
                            const entryInputs = getStepdownEntryInputs(entry)

                            return (
                              <div key={index} className="flex gap-2 items-end">
                                {Object.keys(entryInputs).map((entryKey) => {
                                  const entryInput = entryInputs[entryKey]
                                  return (
                                    <div key={entryKey} className="flex-1">
                                      <RenderInput
                                        input={entryInput}
                                        Key={`${entryKey}-${index}`}
                                        onChange={(_: string, value: any) => {
                                          const convertedValue = entryKey === 'percentage' ? value : Number(value)
                                          updateStepdownEntry(index, entryKey as keyof StepdownEntry, convertedValue)
                                        }}
                                      />
                                    </div>
                                  )
                                })}
                                <button
                                  onClick={() => removeStepdownEntry(index)}
                                  className="text-red-600 hover:text-red-800 p-2 mb-1"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                onClick={() => {
                  setShowAddModal(false)
                  setEditingPenalty(null)
                  setEditingIndex(null)
                  resetForm()
                }}
                className="bg-gray-300 hover:bg-gray-400"
              >
                Cancel
              </Button>
              <Button onClick={editingPenalty ? handleUpdatePenalty : handleAddPenalty}>
                {editingPenalty ? 'Update' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
